/**
 * Enhanced MeetingForm Component
 *
 * Redesigned using the new form primitives for better UX, accessibility,
 * and user-friendly validation messages. Organized into logical sections
 * with comprehensive validation and success states.
 */

import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Place as PlaceIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { z } from 'zod';
import {
  ensureChronology,
  meetingWriteSchema,
  type MeetingWrite,
  type AgendaItem,
  type CoHost,
} from '@penticton-dev-events/shared';
import {
  FormSection,
  FormField,
  FormSuccess,
  FormSuccessPresets,
  ValidationSchemas,
  validateFormValues,
  createInitialFormState,
  type FormState,
} from '../../../components/forms';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useGroups } from '../../groups/hooks/useGroups';
import { useStorageService } from '../../../hooks/useStorageService';
import { usePermissions } from '../../auth/hooks/usePermissions';
import type { GroupMember } from '@penticton-dev-events/shared';

export type MeetingFormProps = {
  readonly groupId: string;
  readonly organizerUid: string;
  readonly onSubmit: (values: MeetingWrite) => Promise<void> | void;
  readonly onCancel?: () => void;
  readonly initialValues?: Partial<MeetingWrite>; // For editing existing meetings
  readonly groupMember?: GroupMember | null; // For permission checks
};

type MeetingFormValues = Omit<MeetingWrite, 'id' | 'createdAt' | 'attendeeCount'>;

const createInitialState = (
  groupId: string,
  organizerUid: string,
): MeetingFormValues => ({
  groupId,
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  eventType: 'in-person',
  onlineUrl: '',
  location: undefined,
  capacity: undefined,
  requiresApproval: false,
  tags: [],
  agenda: [],
  coHosts: [],
  posterUrl: undefined,
  posterAlt: '',
  posterStoragePath: undefined,
  createdBy: organizerUid,
  status: 'scheduled',
});

const isoFromInput = (value: string): string => new Date(value).toISOString();

const MeetingForm = ({
  groupId,
  organizerUid,
  onSubmit,
  onCancel,
  initialValues,
  groupMember,
}: MeetingFormProps) => {
  const { user } = useAuth();
  const { groups } = useGroups();
  const storageService = useStorageService();
  const permissions = usePermissions(groupMember);

  // Determine if we're in edit mode
  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  const [formState, setFormState] = useState<FormState<MeetingFormValues>>(
    createInitialFormState({
      ...createInitialState(groupId, organizerUid),
      ...initialValues,
    }),
  );
  const [showSuccess, setShowSuccess] = useState(false);

  // UI state for dynamic content
  const [newTag, setNewTag] = useState('');
  const [newAgendaItem, setNewAgendaItem] = useState({
    title: '',
    description: '',
    duration: '',
    presenter: '',
  });
  const [newCoHost, setNewCoHost] = useState({
    uid: '',
    displayName: '',
    role: '',
  });
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [posterUploading, setPosterUploading] = useState(false);
  const [posterUploadProgress, setPosterUploadProgress] = useState(0);

  // Get current group for context
  const currentGroup = groups.find(g => g.id === groupId);

  // Check if user has permission to create meetings
  const canCreateMeeting = permissions.canCreateGroupMeetings();
  const isPlatformAdmin = permissions.isPlatformAdmin();

  const updateField = <K extends keyof MeetingFormValues>(
    field: K,
    value: MeetingFormValues[K],
  ) => {
    const newValues = { ...formState.values, [field]: value };

    // Special validation for date chronology
    let chronologyError: string | null = null;
    if (field === 'startsAt' || field === 'endsAt') {
      try {
        if (newValues.startsAt && newValues.endsAt) {
          ensureChronology(
            isoFromInput(newValues.startsAt),
            isoFromInput(newValues.endsAt),
          );
        }
      } catch (error) {
        chronologyError =
          error instanceof Error ? error.message : 'Start time must be before end time';
      }
    }

    // Don't validate with meetingWriteSchema during input (it expects ISO strings)
    // Just update the values and handle chronology errors
    const errors = { ...formState.errors };

    if (chronologyError) {
      errors.endsAt = chronologyError;
    } else if (field === 'startsAt' || field === 'endsAt') {
      // Clear chronology errors when dates are valid
      delete errors.startsAt;
      delete errors.endsAt;
    }

    setFormState(prev => ({
      ...prev,
      values: newValues,
      errors,
      isValid: Object.keys(errors).length === 0,
      isDirty: true,
      touched: { ...prev.touched, [field]: true },
    }));
  };

  // Helper functions for dynamic content
  const addTag = () => {
    if (
      newTag.trim() &&
      !formState.values.tags?.includes(newTag.trim()) &&
      (formState.values.tags?.length ?? 0) < 10
    ) {
      const newTags = [...(formState.values.tags || []), newTag.trim()];
      updateField('tags', newTags);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = formState.values.tags?.filter(tag => tag !== tagToRemove) || [];
    updateField('tags', newTags);
  };

  const addAgendaItem = () => {
    if (!newAgendaItem.title.trim()) return;

    const item: AgendaItem = {
      id: `agenda-${Date.now()}`,
      title: newAgendaItem.title,
      description: newAgendaItem.description || undefined,
      duration: newAgendaItem.duration ? Number(newAgendaItem.duration) : undefined,
      presenter: newAgendaItem.presenter || undefined,
      order: formState.values.agenda?.length ?? 0,
    };

    const newAgenda = [...(formState.values.agenda || []), item];
    updateField('agenda', newAgenda);
    setNewAgendaItem({ title: '', description: '', duration: '', presenter: '' });
  };

  const removeAgendaItem = (id: string) => {
    const newAgenda = formState.values.agenda?.filter(item => item.id !== id) || [];
    updateField('agenda', newAgenda);
  };

  const addCoHost = () => {
    if (!newCoHost.displayName.trim()) return;

    const coHost: CoHost = {
      uid: newCoHost.uid || `temp-${Date.now()}`, // Temporary UID for demo
      displayName: newCoHost.displayName,
      role: newCoHost.role || undefined,
    };

    const newCoHosts = [...(formState.values.coHosts || []), coHost];
    updateField('coHosts', newCoHosts);
    setNewCoHost({ uid: '', displayName: '', role: '' });
  };

  const removeCoHost = (uid: string) => {
    const newCoHosts =
      formState.values.coHosts?.filter(coHost => coHost.uid !== uid) || [];
    updateField('coHosts', newCoHosts);
  };

  const handlePosterUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        setFormState(prev => ({ ...prev, submitError: 'Please select an image file' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormState(prev => ({ ...prev, submitError: 'Image must be less than 5MB' }));
        return;
      }
      setPosterFile(file);
      setFormState(prev => ({ ...prev, submitError: null }));
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFormState(prev => ({ ...prev, isSubmitting: true, submitError: null }));

    void (async () => {
      try {
        const { values } = formState;

        if (!values.startsAt || !values.endsAt) {
          throw new Error('Start and end times are required.');
        }

        // Handle poster upload if a file is selected
        let posterData: { posterUrl?: string; posterStoragePath?: string } = {};

        console.log('🖼️ Poster upload check:', {
          posterFile: posterFile?.name,
          storageService: !!storageService,
          groupId,
        });

        if (posterFile && storageService) {
          console.log('🚀 Starting poster upload...');
          setPosterUploading(true);
          setPosterUploadProgress(0);

          try {
            const uploadResult = await storageService.uploadPosterImage({
              groupId,
              meetingId: 'temp-' + Date.now(), // Will be replaced with actual meeting ID
              file: posterFile,
              altText: values.posterAlt || '',
              onProgress: progress => {
                console.log('📊 Upload progress:', progress.percentage + '%');
                setPosterUploadProgress(progress.percentage);
              },
              onError: error => {
                console.error('❌ Poster upload error:', error);
                throw new Error('Failed to upload poster: ' + error.message);
              },
            });

            console.log('✅ Upload completed:', uploadResult);

            posterData = {
              posterUrl: uploadResult.url,
              posterStoragePath: uploadResult.storagePath,
            };

            console.log('🎯 Poster data set:', posterData);
          } catch (uploadError) {
            console.error('💥 Upload failed:', uploadError);
            setPosterUploading(false);
            throw uploadError;
          } finally {
            setPosterUploading(false);
          }
        }

        const payload: MeetingWrite = meetingWriteSchema.parse({
          groupId,
          title: values.title.trim(),
          description: values.description.trim(),
          startsAt: isoFromInput(values.startsAt),
          endsAt: isoFromInput(values.endsAt),
          eventType: values.eventType,
          location: values.location
            ? {
                label: values.location.label,
                address: values.location.address || undefined,
                mapUrl: values.location.mapUrl || undefined,
              }
            : undefined,
          onlineUrl: values.onlineUrl || undefined,
          capacity: values.capacity || undefined,
          requiresApproval: values.requiresApproval,
          tags: values.tags && values.tags.length > 0 ? values.tags : undefined,
          agenda: values.agenda && values.agenda.length > 0 ? values.agenda : undefined,
          coHosts:
            values.coHosts && values.coHosts.length > 0 ? values.coHosts : undefined,
          posterAlt: values.posterAlt || undefined,
          ...posterData, // Include poster URL and storage path if uploaded
          createdBy: organizerUid,
          status: 'scheduled',
        });

        console.log('📦 Final payload:', payload);

        ensureChronology(payload.startsAt, payload.endsAt);

        await onSubmit(payload);

        // Show success state
        setShowSuccess(true);

        // Only reset form in create mode, not edit mode
        if (!isEditMode) {
          setTimeout(() => {
            setFormState(
              createInitialFormState(createInitialState(groupId, organizerUid)),
            );
            setNewTag('');
            setNewAgendaItem({ title: '', description: '', duration: '', presenter: '' });
            setNewCoHost({ uid: '', displayName: '', role: '' });
            setPosterFile(null);
            setShowSuccess(false);
          }, 3000);
        } else {
          // In edit mode, just hide success message after delay
          setTimeout(() => {
            setShowSuccess(false);
          }, 3000);
        }
      } catch (submissionError) {
        const message =
          submissionError instanceof Error
            ? submissionError.message
            : 'Unable to schedule meeting. Please try again.';
        setFormState(prev => ({ ...prev, submitError: message }));
      } finally {
        setFormState(prev => ({ ...prev, isSubmitting: false }));
      }
    })();
  };

  // Success state
  if (showSuccess) {
    return (
      <FormSuccess
        {...FormSuccessPresets.meetingScheduled(
          () => setShowSuccess(false), // View event action
          () => {
            // Schedule another action
            setFormState(
              createInitialFormState(createInitialState(groupId, organizerUid)),
            );
            setNewTag('');
            setNewAgendaItem({ title: '', description: '', duration: '', presenter: '' });
            setNewCoHost({ uid: '', displayName: '', role: '' });
            setPosterFile(null);
            setShowSuccess(false);
          },
        )}
      />
    );
  }

  const { values, errors, isSubmitting, submitError } = formState;

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} role="form">
      <Stack spacing={4}>
        {/* Permission Notice */}
        {!canCreateMeeting && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Permission Required:</strong> Only group organizers can create
              meetings.
              {!isPlatformAdmin && ' Please contact the group organizer for assistance.'}
            </Typography>
          </Alert>
        )}

        {isPlatformAdmin && canCreateMeeting && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Admin Mode:</strong> You can create meetings as a platform
              administrator.
            </Typography>
          </Alert>
        )}

        <Box
          component="fieldset"
          disabled={!canCreateMeeting}
          sx={{ border: 'none', p: 0, m: 0 }}
        >
          {/* Event Basics */}
          <FormSection
            title="Event Basics"
            subtitle="Essential information about your event"
            required
          >
            <FormField
              name="title"
              label="Event Title"
              value={values.title}
              onChange={value => updateField('title', value)}
              validation={{
                schema: ValidationSchemas.meetingTitle,
                required: true,
              }}
              placeholder="Enter a clear, descriptive title"
              helperText="This title will be shown to all members"
              maxLength={120}
              showCharacterCount
              errorMessage={errors.title}
            />

            <FormField
              name="description"
              label="Event Description"
              type="textarea"
              value={values.description}
              onChange={value => updateField('description', value)}
              validation={{
                schema: ValidationSchemas.meetingDescription,
              }}
              placeholder="Describe what attendees can expect, agenda overview, or call to action"
              helperText="Detailed information to help people understand and get excited about your event"
              minRows={4}
              maxRows={8}
              maxLength={2000}
              showCharacterCount
              errorMessage={errors.description}
            />
          </FormSection>

          {/* Event Type */}
          <FormSection
            title="Event Type"
            subtitle="How attendees will participate"
            required
          >
            <FormControl component="fieldset" error={!!formState.errors.eventType}>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Select event type *
              </FormLabel>
              <RadioGroup
                value={values.eventType}
                onChange={e =>
                  updateField(
                    'eventType',
                    e.target.value as 'in-person' | 'online' | 'hybrid',
                  )
                }
                row
              >
                <FormControlLabel
                  value="in-person"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlaceIcon fontSize="small" />
                      In-Person
                    </Box>
                  }
                />
                <FormControlLabel
                  value="online"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VideocamIcon fontSize="small" />
                      Online
                    </Box>
                  }
                />
                <FormControlLabel
                  value="hybrid"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlaceIcon fontSize="small" />
                      <VideocamIcon fontSize="small" />
                      Hybrid
                    </Box>
                  }
                />
              </RadioGroup>
              {formState.errors.eventType && (
                <FormHelperText>{formState.errors.eventType}</FormHelperText>
              )}
            </FormControl>
          </FormSection>

          {/* Date & Time */}
          <FormSection
            title="Date & Time"
            subtitle="When your event takes place"
            required
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormField
                name="startsAt"
                label="Start Time"
                type="datetime-local"
                value={values.startsAt}
                onChange={value => updateField('startsAt', value)}
                validation={{
                  schema: ValidationSchemas.datetime,
                  required: true,
                }}
                errorMessage={errors.startsAt}
              />
              <FormField
                name="endsAt"
                label="End Time"
                type="datetime-local"
                value={values.endsAt}
                onChange={value => updateField('endsAt', value)}
                validation={{
                  schema: ValidationSchemas.datetime,
                  required: true,
                }}
                helperText="End time must be after start time"
                errorMessage={errors.endsAt}
              />
            </Stack>
          </FormSection>

          {/* Location */}
          <FormSection title="Location" subtitle="Where attendees will join your event">
            {/* Physical Location - Show for in-person and hybrid */}
            {(values.eventType === 'in-person' || values.eventType === 'hybrid') && (
              <>
                <FormField
                  name="locationLabel"
                  label={
                    values.eventType === 'hybrid' ? 'Physical Location *' : 'Location *'
                  }
                  value={values.location?.label ?? ''}
                  onChange={value =>
                    updateField(
                      'location',
                      value
                        ? {
                            label: value,
                            address: values.location?.address,
                            mapUrl: values.location?.mapUrl,
                          }
                        : undefined,
                    )
                  }
                  placeholder="e.g., 'Conference Room A', 'Central Library', 'TechHub Co-working'"
                  helperText="Required for in-person events"
                  validation={{
                    required: true,
                  }}
                  errorMessage={
                    !values.location?.label && formState.touched.location
                      ? 'Location is required for this event type'
                      : undefined
                  }
                />

                {values.location?.label && (
                  <>
                    <FormField
                      name="locationAddress"
                      label="Full Address"
                      value={values.location?.address ?? ''}
                      onChange={value =>
                        updateField('location', {
                          label: values.location!.label,
                          address: value,
                          mapUrl: values.location?.mapUrl,
                        })
                      }
                      placeholder="Complete address for navigation"
                      helperText="Help attendees find your location"
                    />
                    <FormField
                      name="mapUrl"
                      label="Map Link"
                      type="url"
                      value={values.location?.mapUrl ?? ''}
                      onChange={value =>
                        updateField('location', {
                          label: values.location!.label,
                          address: values.location?.address,
                          mapUrl: value,
                        })
                      }
                      placeholder="https://maps.google.com/..."
                      helperText="Google Maps link or similar (optional)"
                    />
                  </>
                )}
              </>
            )}

            {/* Online URL - Show for online and hybrid */}
            {(values.eventType === 'online' || values.eventType === 'hybrid') && (
              <FormField
                name="onlineUrl"
                label={
                  values.eventType === 'hybrid' ? 'Online Meeting URL *' : 'Meeting URL *'
                }
                type="url"
                value={values.onlineUrl ?? ''}
                onChange={value => updateField('onlineUrl', value)}
                placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
                helperText={
                  values.eventType === 'hybrid'
                    ? 'Required for hybrid events - also attending in person'
                    : 'Zoom, Teams, Meet, or other video call link'
                }
                validation={{
                  required: true,
                  schema: ValidationSchemas.url,
                }}
                errorMessage={
                  !values.onlineUrl && formState.touched.onlineUrl
                    ? 'Online meeting URL is required for this event type'
                    : undefined
                }
              />
            )}

            {/* Show helper text when only online */}
            {values.eventType === 'online' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This is an online-only event. Attendees will join via the provided link.
              </Typography>
            )}
          </FormSection>

          {/* Event Logistics */}
          <FormSection
            title="Event Logistics"
            subtitle="Capacity and attendance settings"
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="flex-start"
            >
              <FormField
                name="capacity"
                label="Capacity"
                type="number"
                value={values.capacity ?? ''}
                onChange={value =>
                  updateField('capacity', value ? Number(value) : undefined)
                }
                validation={{
                  schema: ValidationSchemas.capacity,
                }}
                placeholder="Maximum attendees"
                helperText="Leave empty for unlimited"
                inputProps={{ min: 1, max: 10000 }}
              />
              <FormField
                name="requiresApproval"
                label="Require RSVP approval"
                type="switch"
                value={values.requiresApproval}
                onChange={value => updateField('requiresApproval', value)}
                helperText="When enabled, you'll need to approve each RSVP"
              />
            </Stack>
          </FormSection>

          {/* Event Media */}
          <FormSection
            title="Event Media"
            subtitle="Visual assets and promotional materials"
            variant="collapsible"
            defaultExpanded={false}
          >
            <Box>
              {/* Show existing poster if in edit mode */}
              {isEditMode && values.posterUrl && !posterFile && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Poster:
                  </Typography>
                  <Box
                    component="img"
                    src={values.posterUrl}
                    alt={values.posterAlt || 'Event poster'}
                    sx={{
                      width: '100%',
                      maxWidth: 300,
                      height: 'auto',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              )}

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="poster-upload"
                type="file"
                onChange={handlePosterUpload}
              />
              <label htmlFor="poster-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={posterUploading}
                  fullWidth
                >
                  {posterUploading
                    ? `Uploading... ${posterUploadProgress}%`
                    : posterFile
                      ? `Selected: ${posterFile.name}`
                      : isEditMode && values.posterUrl
                        ? 'Replace Event Poster'
                        : 'Upload Event Poster'}
                </Button>
              </label>
            </Box>

            {posterFile && (
              <FormField
                name="posterAlt"
                label="Poster Description"
                value={values.posterAlt ?? ''}
                onChange={value => updateField('posterAlt', value)}
                placeholder="Describe the poster for accessibility"
                helperText="Help screen readers understand your poster content"
                maxLength={200}
                showCharacterCount
              />
            )}
          </FormSection>

          {/* Tags & Categories */}
          <FormSection
            title="Tags & Categories"
            subtitle="Help people discover your event"
            variant="collapsible"
            defaultExpanded={false}
          >
            <Box>
              {values.tags && values.tags.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  {values.tags.map((tag: string) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => removeTag(tag)}
                      size="small"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
              <Stack direction="row" spacing={1}>
                <FormField
                  name="newTag"
                  label="Add Tag"
                  value={newTag}
                  onChange={setNewTag}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  size="small"
                  placeholder="technology, workshop, networking..."
                  helperText="Press Enter to add. Max 10 tags"
                  component={
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <FormField
                          name="tagInput"
                          label=""
                          value={newTag}
                          onChange={setNewTag}
                          size="small"
                          placeholder="Add a tag..."
                        />
                      </Box>
                      <Button
                        onClick={addTag}
                        disabled={!newTag.trim() || (values.tags?.length ?? 0) >= 10}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Stack>
                  }
                />
              </Stack>
            </Box>
          </FormSection>

          {/* Agenda */}
          <FormSection
            title={`Agenda (${values.agenda?.length ?? 0} items)`}
            subtitle="Structure your event with a detailed agenda"
            variant="collapsible"
            defaultExpanded={false}
          >
            <Stack spacing={2}>
              {values.agenda?.map((item, index) => (
                <Card key={item.id} variant="outlined">
                  <CardContent sx={{ pb: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {index + 1}. {item.title}
                        </Typography>
                        {item.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {item.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                          {item.duration && (
                            <Typography variant="caption" color="text.secondary">
                              Duration: {item.duration} min
                            </Typography>
                          )}
                          {item.presenter && (
                            <Typography variant="caption" color="text.secondary">
                              Presenter: {item.presenter}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                      <IconButton
                        onClick={() => removeAgendaItem(item.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}

              <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Add Agenda Item
                  </Typography>
                  <Stack spacing={2}>
                    <FormField
                      name="agendaTitle"
                      label="Title"
                      value={newAgendaItem.title}
                      onChange={value =>
                        setNewAgendaItem(prev => ({ ...prev, title: value }))
                      }
                      size="small"
                      required
                    />
                    <FormField
                      name="agendaDescription"
                      label="Description"
                      type="textarea"
                      value={newAgendaItem.description}
                      onChange={value =>
                        setNewAgendaItem(prev => ({ ...prev, description: value }))
                      }
                      size="small"
                      rows={2}
                    />
                    <Stack direction="row" spacing={1}>
                      <FormField
                        name="agendaDuration"
                        label="Duration (minutes)"
                        type="number"
                        value={newAgendaItem.duration}
                        onChange={value =>
                          setNewAgendaItem(prev => ({ ...prev, duration: value }))
                        }
                        size="small"
                        inputProps={{ min: 1, max: 300 }}
                      />
                      <FormField
                        name="agendaPresenter"
                        label="Presenter"
                        value={newAgendaItem.presenter}
                        onChange={value =>
                          setNewAgendaItem(prev => ({ ...prev, presenter: value }))
                        }
                        size="small"
                      />
                      <Button
                        onClick={addAgendaItem}
                        disabled={!newAgendaItem.title.trim()}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </FormSection>

          {/* Co-hosts */}
          <FormSection
            title={`Co-hosts (${values.coHosts?.length ?? 0})`}
            subtitle="Add collaborators to help organize your event"
            variant="collapsible"
            defaultExpanded={false}
          >
            <Stack spacing={2}>
              {values.coHosts?.map(coHost => (
                <Card key={coHost.uid} variant="outlined">
                  <CardContent sx={{ pb: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {coHost.displayName}
                        </Typography>
                        {coHost.role && (
                          <Typography variant="body2" color="text.secondary">
                            {coHost.role}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        onClick={() => removeCoHost(coHost.uid)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}

              <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Add Co-host
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <FormField
                      name="coHostName"
                      label="Name"
                      value={newCoHost.displayName}
                      onChange={value =>
                        setNewCoHost(prev => ({ ...prev, displayName: value }))
                      }
                      size="small"
                      required
                    />
                    <FormField
                      name="coHostRole"
                      label="Role"
                      value={newCoHost.role}
                      onChange={value => setNewCoHost(prev => ({ ...prev, role: value }))}
                      size="small"
                      placeholder="Speaker, Facilitator, etc."
                    />
                    <Button
                      onClick={addCoHost}
                      disabled={!newCoHost.displayName.trim()}
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
                    >
                      Add
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </FormSection>
        </Box>

        {/* Submit Error */}
        {submitError && (
          <Typography color="error" variant="body2" role="alert">
            {submitError}
          </Typography>
        )}

        {/* Form Actions */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 2 }}>
          {onCancel && (
            <Button onClick={onCancel} variant="text" size="large">
              Cancel
            </Button>
          )}
          <Button
            disabled={
              !canCreateMeeting ||
              isSubmitting ||
              posterUploading ||
              !formState.isValid ||
              !values.title.trim() ||
              !values.startsAt ||
              !values.endsAt
            }
            type="submit"
            variant="contained"
            size="large"
          >
            {posterUploading
              ? `Uploading poster... ${posterUploadProgress}%`
              : isSubmitting
                ? isEditMode
                  ? 'Updating Event...'
                  : 'Creating Event...'
                : isEditMode
                  ? 'Update Event'
                  : 'Create Event'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MeetingForm;
